import type { IntlShape } from 'react-intl';

import { fg } from '@confluence/feature-gating';
import { getRelativeTime } from '@confluence/macro-utils/entry-points/getRelativeTime.ts';

import type { useBlogPostsQueryQuery$variables } from '../hooks/__generated__/useBlogPostsQueryQuery.graphql.ts';
import type { BlogPostsMacroParams, BlogPostNode } from '../blogPostMacroTypes.ts';

const DAY = 86400000;

const DEFAULT_MAX = 100;
const MAX_LIMIT = 200;
export const ENTIRE_MODE_PAGE_SIZE = 5;

const splitMacroValues = (value: string): string[] =>
	value.split(',').flatMap((item) => {
		const trimmed = item.trim();
		return trimmed ? [trimmed] : [];
	});

// Splits on commas and whitespace, for closer parity to legacy param parsing
const splitMacroValuesFixed = (value: string): string[] =>
	value.split(/[,\s]+/).flatMap((item) => {
		const trimmed = item.trim();
		return trimmed ? [trimmed] : [];
	});

const quoteCqlValue = (value: string): string => `'${value}'`;

const createSpaceTypeClause = (spaceType: string, operator = '='): string =>
	`space.type ${operator} ${spaceType}`;

const createSpaceIncludeClause = (spaces: string[], spaceTypeClauses: string[]): string | null => {
	const clauses = [...spaceTypeClauses];
	if (spaces.length > 0) {
		clauses.push(`space IN (${spaces.map(quoteCqlValue).join(',')})`);
	}

	if (clauses.length === 0) {
		return null;
	}

	return clauses.length === 1 ? clauses[0] : `(${clauses.join(' OR ')})`;
};

const addSpaceClauses = (clauses: string[], spacesValue: string, currentSpaceKey?: string) => {
	const spaceKeys = splitMacroValues(spacesValue);
	let includeSpaces: string[] = [];
	let excludeSpaces: string[] = [];

	for (const key of spaceKeys) {
		if (key === '@all') {
			includeSpaces = [];
			excludeSpaces = [];
			break;
		} else if (key === '@self' && currentSpaceKey) {
			includeSpaces.push(currentSpaceKey);
		} else if (key.startsWith('-')) {
			excludeSpaces.push(key.slice(1));
		} else {
			includeSpaces.push(key);
		}
	}

	if (includeSpaces.length > 0) {
		const spaceList = includeSpaces.map(quoteCqlValue).join(',');
		clauses.push(`space IN (${spaceList})`);
	}
	for (const excludeSpace of excludeSpaces) {
		clauses.push(`space != ${quoteCqlValue(excludeSpace)}`);
	}
};

const addFixedSpaceClauses = (clauses: string[], spacesValue: string, currentSpaceKey?: string) => {
	const spaceKeys = splitMacroValuesFixed(spacesValue);
	const includeSpaces: string[] = [];
	const includeSpaceTypeClauses: string[] = [];
	const requiredSpaces: string[] = [];
	const requiredSpaceTypeClauses: string[] = [];
	const excludeSpaces: string[] = [];
	const excludeSpaceTypeClauses: string[] = [];
	let includeAllSpaces = false;

	const addSpaceFilter = (rawKey: string) => {
		const prefix = rawKey[0] === '+' || rawKey[0] === '-' ? rawKey[0] : '';
		const key = prefix ? rawKey.slice(1) : rawKey;
		const targetSpaces =
			prefix === '+' ? requiredSpaces : prefix === '-' ? excludeSpaces : includeSpaces;
		const targetSpaceTypeClauses =
			prefix === '+'
				? requiredSpaceTypeClauses
				: prefix === '-'
					? excludeSpaceTypeClauses
					: includeSpaceTypeClauses;
		const spaceTypeOperator = prefix === '-' ? '!=' : '=';

		if (key === '@all' || key === '*') {
			if (prefix === '-') {
				includeAllSpaces = false;
				targetSpaces.length = 0;
				targetSpaceTypeClauses.length = 0;
			} else {
				includeAllSpaces = true;
			}
		} else if (key === '@self') {
			if (currentSpaceKey) {
				targetSpaces.push(currentSpaceKey);
			}
		} else if (key === '@personal') {
			targetSpaceTypeClauses.push(createSpaceTypeClause('personal', spaceTypeOperator));
		} else if (key === '@global') {
			targetSpaceTypeClauses.push(createSpaceTypeClause('global', spaceTypeOperator));
		} else if (key === '@favorite' || key === '@favourite') {
			targetSpaceTypeClauses.push(createSpaceTypeClause('favorite', spaceTypeOperator));
		} else if (key) {
			targetSpaces.push(key);
		}
	};

	for (const key of spaceKeys) {
		addSpaceFilter(key);
	}

	const includeClause =
		createSpaceIncludeClause(requiredSpaces, requiredSpaceTypeClauses) ??
		(includeAllSpaces ? null : createSpaceIncludeClause(includeSpaces, includeSpaceTypeClauses));
	if (includeClause) {
		clauses.push(includeClause);
	}
	if (excludeSpaces.length > 0) {
		clauses.push(`space NOT IN (${excludeSpaces.map(quoteCqlValue).join(',')})`);
	}
	clauses.push(...excludeSpaceTypeClauses);
};

const addLabelClauses = (clauses: string[], labelsValue: string, matchLabels: string) => {
	const labels = splitMacroValuesFixed(labelsValue);
	const requireLabels: string[] = [];
	const excludeLabels: string[] = [];
	const anyLabels: string[] = [];

	for (const label of labels) {
		if (label.startsWith('+')) {
			requireLabels.push(label.slice(1));
		} else if (label.startsWith('-')) {
			excludeLabels.push(label.slice(1));
		} else {
			anyLabels.push(label);
		}
	}

	for (const label of requireLabels) {
		clauses.push(`label = ${quoteCqlValue(label)}`);
	}

	for (const label of excludeLabels) {
		clauses.push(`label != ${quoteCqlValue(label)}`);
	}

	if (anyLabels.length > 0) {
		if (matchLabels === 'all') {
			for (const label of anyLabels) {
				clauses.push(`label = ${quoteCqlValue(label)}`);
			}
		} else {
			const labelList = anyLabels.map(quoteCqlValue).join(',');
			clauses.push(`label IN (${labelList})`);
		}
	}
};

const addFixedLabelClauses = (clauses: string[], labelsValue: string, matchLabels: string) => {
	const labels = splitMacroValues(labelsValue);
	const requireLabels: string[] = [];
	const excludeLabels: string[] = [];
	const anyLabels: string[] = [];

	for (const label of labels) {
		if (label.startsWith('+')) {
			requireLabels.push(label.slice(1));
		} else if (label.startsWith('-')) {
			excludeLabels.push(label.slice(1));
		} else {
			anyLabels.push(label);
		}
	}

	for (const label of requireLabels) {
		clauses.push(`label = ${quoteCqlValue(label)}`);
	}

	if (excludeLabels.length > 0) {
		clauses.push(`label NOT IN (${excludeLabels.map(quoteCqlValue).join(',')})`);
	}

	if (anyLabels.length > 0) {
		if (matchLabels === 'all') {
			for (const label of anyLabels) {
				clauses.push(`label = ${quoteCqlValue(label)}`);
			}
		} else {
			const labelList = anyLabels.map(quoteCqlValue).join(',');
			clauses.push(`label IN (${labelList})`);
		}
	}
};

/**
 * Parses a relative time string like "7d", "14d", "1w", "2h" etc.
 * and returns milliseconds.
 */
const parseRelativeTime = (time: string): number | null => {
	const match = time.match(/^(\d+)([smhdwMy])$/);
	if (!match) {
		return null;
	}
	const value = parseInt(match[1], 10);
	const unit = match[2];
	const multipliers: Record<string, number> = {
		s: 1000,
		m: 60 * 1000,
		h: 60 * 60 * 1000,
		d: 24 * 60 * 60 * 1000,
		w: 7 * 24 * 60 * 60 * 1000,
		M: 30 * 24 * 60 * 60 * 1000,
		y: 365 * 24 * 60 * 60 * 1000,
	};
	return value * (multipliers[unit] ?? 0);
};

const getCurrentTime = (): number => {
	if (window.__SSR_CURRENT_DATE__) {
		return window.__SSR_CURRENT_DATE__;
	}

	return Date.now();
};

/**
 * Builds a CQL query string from the macro parameters.
 */
export const buildCqlQuery = (params: BlogPostsMacroParams, currentSpaceKey?: string): string => {
	const clauses: string[] = ["type = 'blogpost'"];

	// Space filter
	const spacesValue = params.spaces?.value ?? params.space?.value;
	if (spacesValue) {
		if (fg('cc_rewrite_blog_posts_legacy_macro_fix_param_bug')) {
			addFixedSpaceClauses(clauses, spacesValue, currentSpaceKey);
		} else {
			addSpaceClauses(clauses, spacesValue, currentSpaceKey);
		}
	} else if (currentSpaceKey) {
		// Default: current space
		clauses.push(`space = ${quoteCqlValue(currentSpaceKey)}`);
	}

	// Labels filter
	const labelsValue = params.labels?.value ?? params.label?.value;
	const matchLabels = params['match-labels']?.value ?? 'any';
	if (labelsValue) {
		if (fg('cc_rewrite_blog_posts_legacy_macro_fix_param_bug')) {
			addFixedLabelClauses(clauses, labelsValue, matchLabels);
		} else {
			addLabelClauses(clauses, labelsValue, matchLabels);
		}
	}

	// Time filter
	const timeValue = params.time?.value;
	if (timeValue) {
		const ms = parseRelativeTime(timeValue);
		if (ms) {
			// Ensures date calculation matches in SPA context, if SSR rendered,
			// such that resulting cql matches
			const currentTime = getCurrentTime();
			const then = new Date(currentTime - ms);
			clauses.push(`lastModified >= '${then.toISOString().split('T')[0]}'`);
		}
	}

	// Author filter
	const authorValue = params.author?.value;
	if (authorValue) {
		const authors = splitMacroValues(authorValue);
		if (authors.length === 1) {
			clauses.push(`creator = ${quoteCqlValue(authors[0])}`);
		} else if (authors.length > 1) {
			const authorList = authors.map(quoteCqlValue).join(',');
			clauses.push(`creator IN (${authorList})`);
		}
	}

	// Sort
	const sortValue = params.sort?.value;
	const reverseValue = params.reverse?.value === 'true';
	let orderClause = 'ORDER BY created DESC'; // default

	if (sortValue === 'title') {
		orderClause = `ORDER BY title ${reverseValue ? 'DESC' : 'ASC'}`;
	} else if (sortValue === 'creation') {
		orderClause = `ORDER BY created ${reverseValue ? 'DESC' : 'ASC'}`;
	} else if (sortValue === 'modified') {
		orderClause = `ORDER BY lastModified ${reverseValue ? 'DESC' : 'ASC'}`;
	}

	return `${clauses.join(' AND ')} ${orderClause}`;
};

/**
 * Builds the GraphQL query variables for the blog posts macro query from
 * macro parameters. Shared between the runtime React hook and the SSR
 * preload handler so the logic is defined in a single place.
 */
export const buildQueryVariables = (
	params: BlogPostsMacroParams,
	currentSpaceKey?: string,
): useBlogPostsQueryQuery$variables => {
	const contentMode = params.content?.value ?? 'entire';
	const max = getMaxResults(params);
	const cql = buildCqlQuery(params, currentSpaceKey);
	const includeEntireBody = contentMode === 'entire';
	const includeExcerpt = contentMode === 'excerpts';
	const includeAdfMediaToken = includeEntireBody || includeExcerpt;
	const first = includeEntireBody ? Math.min(max, ENTIRE_MODE_PAGE_SIZE) : max;

	return {
		after: undefined,
		cql,
		first,
		includeEntireBody,
		includeExcerpt,
		includeAdfMediaToken,
	};
};

export const getMaxResults = (params: BlogPostsMacroParams): number => {
	const maxValue = params.max?.value ?? params.maxResults?.value ?? String(DEFAULT_MAX);
	return Math.min(parseInt(maxValue, 10) || DEFAULT_MAX, MAX_LIMIT);
};

export const isBlogPostNode = (node: BlogPostNode | null | undefined): node is BlogPostNode =>
	node != null;

export const getContentUrl = (node: BlogPostNode): string => {
	const links = node.content?.links;
	if (links?.webui) {
		return `/wiki${links.webui}`;
	}
	return '#';
};

export const getSpaceUrl = (node: BlogPostNode): string => {
	const spaceKey = node.content?.space?.key;
	if (spaceKey) {
		return `/wiki/spaces/${spaceKey}`;
	}
	return '#';
};

export const getAuthorName = (node: BlogPostNode): string =>
	node.content?.history?.createdBy?.displayName ?? 'Unknown';

export const getAuthorAccountId = (node: BlogPostNode): string | undefined => {
	const createdBy = node.content?.history?.createdBy;
	if (createdBy && 'accountId' in createdBy) {
		return createdBy.accountId ?? undefined;
	}
	return undefined;
};

export const getProfileUrl = (node: BlogPostNode): string => {
	const accountId = getAuthorAccountId(node);
	if (accountId) {
		return `/wiki/people/${accountId}?ref=confluence`;
	}
	return '#';
};

export const getAuthorAvatarUrl = (node: BlogPostNode): string | undefined => {
	const createdBy = node.content?.history?.createdBy;
	if (createdBy && 'profilePicture' in createdBy) {
		return createdBy.profilePicture?.path ?? undefined;
	}
	return undefined;
};

export const getCreatedDate = (node: BlogPostNode): string =>
	node.content?.history?.createdDate ?? '';

export const getLabels = (node: BlogPostNode): string[] => {
	const result: string[] = [];
	const nodes = node.content?.labels?.nodes;
	if (!nodes) {
		return result;
	}
	for (const label of nodes) {
		if (label?.name) {
			result.push(label.name);
		}
	}
	return result;
};

export const getLabelUrl = (label: string, spaceKey?: string | null): string => {
	if (spaceKey) {
		return `/wiki/label/${spaceKey}/${encodeURIComponent(label)}`;
	}
	return `/wiki/label/${encodeURIComponent(label)}`;
};

export const formatBlogDate = (dateString: string, intl: IntlShape, timeZone?: string): string => {
	const date = new Date(dateString);
	return intl.formatDate(date, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		...(fg('cc-dlp-fix-blog-posts-macro-ssr-date-bug') ? { timeZone } : {}),
	});
};

/**
 * Returns a relative time string (e.g. "3 hours ago") for dates less than 1 day old,
 * otherwise falls back to an absolute formatted date (e.g. "Jan 15, 2025").
 *
 * This mirrors the legacy BlogPostsMacro behavior where FriendlyDateFormatter only
 * uses relative time for recent content (~1 day), then switches to a short date.
 */
export const formatSmartDate = (dateString: string, intl: IntlShape, timeZone?: string): string => {
	const timestamp = new Date(dateString).getTime();
	const diff = Date.now() - timestamp;

	if (diff < DAY) {
		return getRelativeTime(timestamp, intl);
	}

	return formatBlogDate(dateString, intl, timeZone);
};
